// Suivi de hauteur et détection d'attaques pour des notes isolées de piano.
//
// Principe : à chaque saut (hop, ~16 ms) on mesure le niveau (RMS) et la part
// d'aigus (différence première) sur les derniers échantillons ; une montée nette du
// niveau ou des aigus par rapport à l'enveloppe récente signale une nouvelle frappe
// (attaque). Après une attaque, la hauteur est estimée sur une fenêtre courte (64 ms)
// et la note n'est émise que lorsque deux estimations consécutives concordent avec
// une clarté élevée. Sinon, l'événement est émis comme incertain ; une attaque sans
// hauteur reconnue est émise à part.
//
// Le suivi est aveugle : il ne reçoit jamais la partition. La validation d'une note
// attendue se fait plus loin, dans l'appariement.

import { Decimator, chooseDecimationFactor } from './decimator';
import { estimatePitch } from './mpm';
import { frequencyToMidiFloat } from '../../music/pitchNames';
import type { Confidence } from '../types';

export interface TrackerOptions {
  /** Taux d'échantillonnage du signal fourni à process(). */
  sampleRate: number;
  /** Taux d'analyse visé après sous-échantillonnage (défaut 16 000 Hz). */
  analysisRate?: number;
  /** Fenêtre d'analyse de hauteur, en échantillons au taux d'analyse (défaut 1024). */
  windowSize?: number;
  /** Saut entre deux analyses, en échantillons au taux d'analyse (défaut 256). */
  hopSize?: number;
  minFrequency?: number;
  maxFrequency?: number;
  /** Montée du niveau (dB) qui signale une attaque. */
  onsetRiseDb?: number;
  /** Montée de la part d'aigus (dB) qui signale une attaque (utile pour les notes répétées). */
  onsetRiseHfDb?: number;
  /** Niveau minimal au-dessus du plancher de bruit pour accepter une attaque. */
  minLevelAboveNoiseDb?: number;
  /** Niveau absolu (dB) en dessous duquel rien n'est jamais considéré comme une note. */
  absoluteMinDb?: number;
  /** Durée minimale (en sauts) d'une attaque sans hauteur pour qu'elle soit signalée (écarte les clics brefs). */
  minOnsetFrames?: number;
  clarityHigh?: number;
  clarityLow?: number;
  /** Écart maximal (cents) entre deux estimations consécutives pour les juger stables. */
  stableCents?: number;
  /** Nombre d'estimations concordantes nécessaires. */
  stableFrames?: number;
  /** Attente maximale (en sauts) après une attaque avant de conclure. */
  maxPitchWaitFrames?: number;
  /** Délai minimal entre deux attaques. */
  refractoryMs?: number;
  /** Décroissance de l'enveloppe de niveau par saut, en dB. */
  releaseDbPerFrame?: number;
  releaseHfDbPerFrame?: number;
}

export type TrackerEvent =
  | { type: 'noteOn'; pitch: number; time: number; confidence: Confidence; cents: number; clarity: number; levelDb: number }
  | { type: 'noteOff'; pitch: number; time: number }
  | { type: 'onset'; time: number; levelDb: number };

interface Candidate {
  midi: number;
  clarity: number;
}

interface PendingOnset {
  frame: number;
  time: number;
  levelDb: number;
  candidates: (Candidate | null)[];
  /** Nombre de sauts pendant lesquels le niveau est resté suffisant. */
  sustained: number;
}

interface ActiveNote {
  pitch: number;
  peakDb: number;
  /** Estimations récentes qui divergent de la note active (changement sans attaque). */
  divergent: Candidate[];
}

const DB_FLOOR = -100;

export function toDb(rms: number): number {
  return 20 * Math.log10(rms + 1e-7);
}

export class PitchTracker {
  readonly inputRate: number;
  readonly analysisRate: number;
  readonly windowSize: number;
  readonly hopSize: number;
  private readonly decimator: Decimator;
  private readonly ring: Float32Array;
  private readonly window: Float32Array;
  private readonly scratch: Float32Array;
  private ringPos = 0;
  private filled = 0;
  private sinceHop = 0;
  private frame = 0;
  private readonly frameMs: number;

  private readonly minFrequency: number;
  private readonly maxFrequency: number;
  private readonly onsetRiseDb: number;
  private readonly onsetRiseHfDb: number;
  private readonly minLevelAboveNoiseDb: number;
  private readonly absoluteMinDb: number;
  private readonly minOnsetFrames: number;
  private readonly clarityHigh: number;
  private readonly clarityLow: number;
  private readonly stableCents: number;
  private readonly stableFrames: number;
  private readonly maxPitchWaitFrames: number;
  private readonly refractoryFrames: number;
  private readonly releaseDbPerFrame: number;
  private readonly releaseHfDbPerFrame: number;

  private env = DB_FLOOR;
  private hfEnv = DB_FLOOR;
  private dbPrev1 = DB_FLOOR;
  private dbPrev2 = DB_FLOOR;
  private hfPrev1 = DB_FLOOR;
  private hfPrev2 = DB_FLOOR;
  private noise = -70;
  private lastOnsetFrame = -Infinity;
  private pending: PendingOnset | null = null;
  private active: ActiveNote | null = null;

  /** Dernier niveau mesuré (dB). */
  levelDb = DB_FLOOR;
  /** Plancher de bruit estimé (dB). */
  noiseFloorDb = -70;
  /** Dernière estimation de hauteur (note MIDI fractionnaire) ou null. */
  lastMidi: number | null = null;
  lastClarity = 0;

  constructor(options: TrackerOptions) {
    this.inputRate = options.sampleRate;
    const analysisTarget = options.analysisRate ?? 16000;
    const factor = chooseDecimationFactor(options.sampleRate, analysisTarget);
    this.decimator = new Decimator(options.sampleRate, factor);
    this.analysisRate = this.decimator.outputRate;
    this.windowSize = options.windowSize ?? 1024;
    this.hopSize = options.hopSize ?? 256;
    this.frameMs = (this.hopSize / this.analysisRate) * 1000;
    this.ring = new Float32Array(this.windowSize);
    this.window = new Float32Array(this.windowSize);
    this.minFrequency = options.minFrequency ?? 50;
    this.maxFrequency = options.maxFrequency ?? 2200;
    this.scratch = new Float32Array(Math.ceil(this.analysisRate / this.minFrequency) + 2);
    this.onsetRiseDb = options.onsetRiseDb ?? 6;
    this.onsetRiseHfDb = options.onsetRiseHfDb ?? 8;
    this.minLevelAboveNoiseDb = options.minLevelAboveNoiseDb ?? 12;
    this.absoluteMinDb = options.absoluteMinDb ?? -62;
    this.minOnsetFrames = options.minOnsetFrames ?? 3;
    this.clarityHigh = options.clarityHigh ?? 0.9;
    this.clarityLow = options.clarityLow ?? 0.7;
    this.stableCents = options.stableCents ?? 50;
    this.stableFrames = options.stableFrames ?? 2;
    this.maxPitchWaitFrames = options.maxPitchWaitFrames ?? 8;
    this.refractoryFrames = Math.max(1, Math.ceil((options.refractoryMs ?? 60) / this.frameMs));
    this.releaseDbPerFrame = options.releaseDbPerFrame ?? 0.5;
    this.releaseHfDbPerFrame = options.releaseHfDbPerFrame ?? 1.0;
  }

  /** Note active (hauteur MIDI) telle que suivie par le détecteur, ou null. */
  get activePitch(): number | null {
    return this.active ? this.active.pitch : null;
  }

  /**
   * Traite un bloc d'échantillons. `startTimeMs` est l'instant du premier échantillon
   * sur l'horloge performance.now(). Renvoie les événements produits.
   */
  process(samples: Float32Array, startTimeMs: number): TrackerEvent[] {
    const events: TrackerEvent[] = [];
    // Chaque échantillon conservé correspond à un échantillon d'entrée précis du paquet :
    // le premier est à l'indice `phase`, les suivants tous les `factor` échantillons. Les
    // instants sont calculés sur l'indice d'entrée, ce qui les rend indépendants de la
    // taille des paquets et du taux d'échantillonnage.
    const factor = this.decimator.factor;
    const phase = this.decimator.phase;
    const msPerInputSample = 1000 / this.inputRate;
    const decimated = this.decimator.process(samples);
    for (let k = 0; k < decimated.length; k++) {
      this.ring[this.ringPos] = decimated[k];
      this.ringPos = (this.ringPos + 1) % this.windowSize;
      if (this.filled < this.windowSize) this.filled++;
      this.sinceHop++;
      if (this.sinceHop >= this.hopSize && this.filled >= this.windowSize) {
        this.sinceHop = 0;
        // Fin du saut courant : instant de la fin du dernier échantillon d'entrée conservé.
        const inputIndex = phase + k * factor;
        const hopEnd = startTimeMs + (inputIndex + 1) * msPerInputSample;
        this.analyze(hopEnd, events);
      }
    }
    return events;
  }

  private copyWindow(): void {
    const n = this.windowSize;
    const start = this.ringPos; // plus ancien échantillon
    const firstPart = n - start;
    this.window.set(this.ring.subarray(start, n), 0);
    this.window.set(this.ring.subarray(0, start), firstPart);
  }

  /**
   * Instant de l'attaque à l'intérieur des deux derniers sauts : premier bloc de 32 échantillons
   * dont le niveau dépasse nettement le niveau d'avant la montée. Précision de l'ordre de 2 ms
   * sur une attaque franche ; à défaut, début du dernier saut.
   */
  private refineOnsetTime(hopEnd: number, preLevelDb: number): number {
    const w = this.window;
    const n = w.length;
    const block = 32;
    const region = Math.min(n, this.hopSize * 2);
    const msPerSample = 1000 / this.analysisRate;
    const threshold = Math.max(preLevelDb + 6, this.absoluteMinDb - 6);
    for (let j = n - region; j + block <= n; j += block) {
      let sum = 0;
      for (let i = j; i < j + block; i++) sum += w[i] * w[i];
      if (toDb(Math.sqrt(sum / block)) >= threshold) return hopEnd - (n - j) * msPerSample;
    }
    return hopEnd - this.frameMs;
  }

  private analyze(hopEnd: number, events: TrackerEvent[]): void {
    const time = hopEnd - this.frameMs;
    this.copyWindow();
    const w = this.window;
    const n = w.length;
    const span = Math.min(n, this.hopSize * 2);
    let sum = 0;
    let hfSum = 0;
    for (let i = n - span; i < n; i++) {
      const x = w[i];
      sum += x * x;
      const d = x - w[i - 1];
      hfSum += d * d;
    }
    const db = toDb(Math.sqrt(sum / span));
    const hfDb = toDb(Math.sqrt(hfSum / span));
    this.frame++;
    this.levelDb = db;

    // Plancher de bruit : suit les minima, remonte lentement.
    this.noise = Math.min(db, this.noise + 0.02);
    if (this.noise < -90) this.noise = -90;
    this.noiseFloorDb = this.noise;

    const envPrev = this.env;
    const hfEnvPrev = this.hfEnv;
    this.env = Math.max(db, this.env - this.releaseDbPerFrame);
    this.hfEnv = Math.max(hfDb, this.hfEnv - this.releaseHfDbPerFrame);

    const loudEnough = db >= this.noise + this.minLevelAboveNoiseDb && db >= this.absoluteMinDb;
    // Une attaque peut être à cheval sur deux sauts : on compare aussi au niveau d'il y a deux sauts.
    const preLevelDb = Math.min(envPrev, this.dbPrev2);
    const rise = Math.max(db - envPrev, db - this.dbPrev2);
    const hfRise = Math.max(hfDb - hfEnvPrev, hfDb - this.hfPrev2);
    this.dbPrev2 = this.dbPrev1;
    this.dbPrev1 = db;
    this.hfPrev2 = this.hfPrev1;
    this.hfPrev1 = hfDb;
    const onset =
      loudEnough &&
      this.frame - this.lastOnsetFrame >= this.refractoryFrames &&
      (rise >= this.onsetRiseDb || (hfRise >= this.onsetRiseHfDb && rise >= 1));

    if (onset) {
      if (this.pending) this.resolvePending(events, true);
      this.pending = { frame: this.frame, time: this.refineOnsetTime(hopEnd, preLevelDb), levelDb: db, candidates: [], sustained: 0 };
      this.lastOnsetFrame = this.frame;
    }

    // Estimation de hauteur seulement quand il y a du signal.
    let candidate: Candidate | null = null;
    if (db >= this.noise + 8) {
      const est = estimatePitch(w, this.analysisRate, { minFrequency: this.minFrequency, maxFrequency: this.maxFrequency, threshold: 0.9 }, this.scratch);
      if (est && est.clarity >= this.clarityLow) {
        candidate = { midi: frequencyToMidiFloat(est.frequency), clarity: est.clarity };
      }
      this.lastMidi = est ? frequencyToMidiFloat(est.frequency) : null;
      this.lastClarity = est ? est.clarity : 0;
    } else {
      this.lastMidi = null;
      this.lastClarity = 0;
    }

    if (this.pending) {
      if (this.frame > this.pending.frame) this.pending.candidates.push(candidate);
      if (loudEnough) this.pending.sustained++;
      this.checkPending(events, db);
    } else if (this.active) {
      this.followActive(events, candidate, db, rise, time);
    }

    // Extinction de la note active quand le signal retombe.
    if (this.active) {
      this.active.peakDb = Math.max(this.active.peakDb, db);
      if (db < this.noise + 6 || db < this.active.peakDb - 40) {
        events.push({ type: 'noteOff', pitch: this.active.pitch, time });
        this.active = null;
      }
    }
  }

  private checkPending(events: TrackerEvent[], db: number): void {
    const p = this.pending;
    if (!p) return;
    const c = p.candidates;
    const k = this.stableFrames;
    if (c.length >= k) {
      const last = c.slice(-k);
      if (last.every((x): x is Candidate => x !== null && x.clarity >= this.clarityHigh)) {
        const midis = last.map((x) => x.midi);
        const spread = Math.max(...midis) - Math.min(...midis);
        if (spread * 100 <= this.stableCents) {
          const mean = midis.reduce((a, b) => a + b, 0) / midis.length;
          this.emitNote(events, Math.round(mean), (mean - Math.round(mean)) * 100, last[last.length - 1].clarity, 'sure', p, db);
          this.pending = null;
          return;
        }
      }
    }
    if (this.frame - p.frame >= this.maxPitchWaitFrames) this.resolvePending(events, false);
  }

  /** Conclut une attaque en attente sans stabilité : note incertaine ou attaque sans hauteur. */
  private resolvePending(events: TrackerEvent[], interrupted: boolean): void {
    const p = this.pending;
    if (!p) return;
    this.pending = null;
    const valid = p.candidates.filter((x): x is Candidate => x !== null);
    if (valid.length === 0) {
      // Un bruit bref (clic, choc) ne dure pas : on ne signale que les attaques soutenues sans hauteur.
      if (!interrupted && p.sustained >= this.minOnsetFrames) events.push({ type: 'onset', time: p.time, levelDb: p.levelDb });
      return;
    }
    // Hauteur la plus fréquente parmi les estimations, arrondie au demi-ton.
    const counts = new Map<number, number>();
    for (const v of valid) counts.set(Math.round(v.midi), (counts.get(Math.round(v.midi)) ?? 0) + 1);
    let best = valid[0];
    let bestCount = 0;
    for (const v of valid) {
      const count = counts.get(Math.round(v.midi)) ?? 0;
      if (count > bestCount || (count === bestCount && v.clarity > best.clarity)) {
        best = v;
        bestCount = count;
      }
    }
    this.emitNote(events, Math.round(best.midi), (best.midi - Math.round(best.midi)) * 100, best.clarity, 'uncertain', p, p.levelDb);
  }

  private emitNote(events: TrackerEvent[], pitch: number, cents: number, clarity: number, confidence: Confidence, p: PendingOnset, db: number): void {
    if (this.active) {
      events.push({ type: 'noteOff', pitch: this.active.pitch, time: p.time });
    }
    events.push({ type: 'noteOn', pitch, time: p.time, confidence, cents, clarity, levelDb: p.levelDb });
    this.active = { pitch, peakDb: Math.max(db, p.levelDb), divergent: [] };
  }

  /**
   * Note active sans attaque en attente : détecte un changement de hauteur net et durable
   * (attaque manquée, frappe douce). Une note tenue qui décroît ne doit rien produire.
   */
  private followActive(events: TrackerEvent[], candidate: Candidate | null, db: number, rise: number, time: number): void {
    const a = this.active;
    if (!a) return;
    if (candidate && candidate.clarity >= this.clarityHigh && Math.abs(candidate.midi - a.pitch) >= 0.6) {
      a.divergent.push(candidate);
    } else {
      a.divergent = [];
      return;
    }
    if (a.divergent.length >= this.stableFrames + 1) {
      const midis = a.divergent.map((x) => x.midi);
      const spread = Math.max(...midis) - Math.min(...midis);
      if (spread * 100 <= this.stableCents && db >= this.noise + this.minLevelAboveNoiseDb) {
        const mean = midis.reduce((x, y) => x + y, 0) / midis.length;
        const pitch = Math.round(mean);
        // Sans montée de niveau, on ne peut pas exclure un artefact de la décroissance : incertain.
        const confidence: Confidence = rise >= 2 ? 'sure' : 'uncertain';
        events.push({ type: 'noteOff', pitch: a.pitch, time });
        events.push({ type: 'noteOn', pitch, time, confidence, cents: (mean - pitch) * 100, clarity: a.divergent[a.divergent.length - 1].clarity, levelDb: db });
        this.active = { pitch, peakDb: db, divergent: [] };
        this.lastOnsetFrame = this.frame;
      } else {
        a.divergent = [];
      }
    }
  }
}
